/**
 * DashboardRecentImages - Recent images gallery section
 *
 * WCAG 2.1 Level AA + BFSG konform
 * Light/Dark Mode Support
 */

import { Link } from 'react-router-dom';
import { Images, Sparkles, ArrowRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button, EmptyState } from '@/components/v2/ui';
import { EID } from '@/lib/element-ids.registry';

interface RecentImage {
  id: string;
  preset_type: string;
  created_at: string;
  batch_count?: number | null;
}

interface DashboardRecentImagesProps {
  images: RecentImage[];
}

export function DashboardRecentImages({ images }: DashboardRecentImagesProps) {
  const { t } = useTranslation();
  return (
    <section id={EID['dashboard-images-section']} aria-label={t('dashboard.recentImages')}>
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-sm font-semibold text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">
          {t('dashboard.recentImages')}
        </h2>
        {images.length > 0 && (
          <Link
            id={EID['dashboard-images-gallery-link']}
            to="/v2/tools"
            className="flex items-center gap-1 text-sm text-primary-600 dark:text-primary-400 hover:underline"
          >
            {t('dashboard.openGallery', 'Galerie öffnen')}
            <ArrowRight className="h-3.5 w-3.5" />
          </Link>
        )}
      </div>

      {images.length > 0 ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2.5">
          {images.map((image) => (
            <div
              key={image.id}
              className="group relative rounded-xl overflow-hidden aspect-square bg-neutral-200 dark:bg-neutral-800"
            >
              <img
                src={`/image/${image.id}`}
                alt={t('readiness.generatedImage')}
                loading="lazy"
                className="h-full w-full object-cover transition-transform group-hover:scale-105"
              />
              {image.batch_count != null && image.batch_count > 1 && (
                <span
                  id={EID['dashboard-images-batch-badge']}
                  className="absolute top-1.5 right-1.5 rounded-md bg-black/70 px-1.5 py-0.5 text-2xs font-semibold text-white backdrop-blur-sm"
                >
                  ×{image.batch_count}
                </span>
              )}
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
                <div className="absolute bottom-0 left-0 right-0 p-3">
                  <p className="text-xs font-semibold text-white truncate">
                    {image.preset_type}
                  </p>
                  <p className="text-xs text-neutral-300">{image.created_at}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <EmptyState
          icon={<Images className="h-16 w-16" />}
          title={t('dashboard.noImagesYet')}
          description={t('dashboard.noImagesDescription')}
          action={
            <Link to="/v2/tools">
              <Button id={EID['dashboard-images-create-btn']} variant="primary">
                <Sparkles className="h-4 w-4 mr-2" />
                {t('dashboard.createFirstImage')}
              </Button>
            </Link>
          }
          size="md"
        />
      )}
    </section>
  );
}
